import { useState } from 'react'
import type { JSX } from 'react'
import { Link, useSearchParams } from 'react-router'

import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import type { Query } from '@/api/query'
import { useBrand } from '@/brand/context'
import { elapsed, formatClock, formatDateTime } from '@/lib/format'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { Modal } from '@/ui/Modal'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { summariseThresholds } from '@/settings/content-quality/summary'
import { useToast } from '@/ui/toast-context'
import { rewriteUrlError } from './rewrite'

type Document = Schema<'ContentDocumentOut'>
type Detail = Schema<'ContentDetailOut'>
type Gate = Schema<'QualityGateOut'>
type Check = Schema<'QualityCheckOut'>
type Run = Schema<'ContentRunOut'>

/** The tenant's time zone is the only one this product renders in (CLAUDE.md). */
const IST = 'Asia/Kolkata'

/**
 * The board's four columns, and the statuses each one holds.
 *
 * They are `app/db/seo_content.py`'s `CONTENT_*` constants, and the mapping is
 * one-to-one apart from two deliberate joins:
 *
 *  * **`quality_gate` and `quality_failed` are one column.** They are the same
 *    moment from the tenant's side — *the gate has it* — and the difference is
 *    only whether the verdict has landed. Splitting them would give the board a
 *    column that is empty except for the seconds a check takes.
 *  * **`failed` sits under *Writing*.** It is where a generation that never
 *    produced text died, and `fail_draft` writes the reason onto the same
 *    `quality` column the gate uses so the card can say what went wrong. The
 *    alternative — leaving it off the board — hides an article the tenant paid
 *    for, which is the one thing a pipeline screen must not do.
 *
 * Everything after approval (`approved`, `publishing`, `published`, `rejected`)
 * is deliberately absent: the approval queue and *Where we publish* are those
 * documents' screens, and a board that carried them would never stop growing.
 */
const COLUMNS = [
  {
    key: 'briefed',
    label: 'Briefed',
    statuses: ['brief'],
    empty: 'Nothing briefed. Ask for an article below and it appears here.',
  },
  {
    key: 'writing',
    label: 'Writing',
    statuses: ['drafting', 'failed'],
    empty: 'Nothing being written right now.',
  },
  {
    key: 'gate',
    label: 'Quality gate',
    statuses: ['quality_gate', 'quality_failed'],
    empty: 'Nothing is being checked.',
  },
  {
    key: 'awaiting',
    label: 'Awaiting you',
    statuses: ['awaiting_approval'],
    empty: 'Nothing is waiting on you.',
  },
] as const

/** Every status the board draws, as the repeatable `?status=` the list takes. */
const PIPELINE = COLUMNS.flatMap((column) => [...column.statuses])

/**
 * What the two list endpoints will return at most, from `openapi.json`, where
 * `default` and `maximum` are the same number on both — so this is the cap in
 * force even though neither call sends a `limit`.
 *
 * Said on screen rather than left implicit (WEB-16): neither route offers a
 * cursor, so a site past the cap silently loses the rest, and a board that shows
 * 100 of 140 documents while claiming to be *the* pipeline is the kind of wrong
 * a tenant only finds out about from a missing article. Real paging is a
 * backend ask; naming the cap is what this side can do honestly.
 */
const DOCUMENT_LIMIT = 100
const RUN_LIMIT = 30

/** The five checks `QualityCheckOut.code` names, in the order the mockup lists
 *  them. A code the server adds later still renders — see `gateRows`. */
const CHECKS: Record<string, string> = {
  originality: 'Originality',
  fact_check: 'Fact check',
  brand_voice: 'Brand voice',
  eeat: 'E-E-A-T',
  internal_links: 'Internal links',
}

const CHECK_ORDER = Object.keys(CHECKS)

const VERDICT: Record<string, { tone: string; mark: string }> = {
  pass: { tone: 'ar-badge--success', mark: '✓' },
  warn: { tone: 'ar-badge--warning', mark: '⚠' },
  fail: { tone: 'ar-badge--danger', mark: '✕' },
}

const RUN_STATE: Record<string, { tone: string; label: string }> = {
  queued: { tone: 'ar-badge--neutral', label: 'Queued' },
  running: { tone: 'ar-badge--info', label: 'Running' },
  succeeded: { tone: 'ar-badge--success', label: 'Done' },
  partial: { tone: 'ar-badge--warning', label: 'Partly done' },
  failed: { tone: 'ar-badge--danger', label: 'Failed' },
}

/**
 * The gate's checks in a stable order, whatever order they were stored in.
 *
 * `content.quality` is a `jsonb` column that has held three shapes across two
 * sessions (`_gate_out` says so and reads it defensively for that reason), so
 * the order of `checks` is not something a screen may rely on — and a list whose
 * rows move between two documents is one nobody can compare. Anything the server
 * adds later that this file has no label for is kept, at the end, under its own
 * code: dropping a check the gate ran would be the screen quietly under-reporting
 * why a draft was blocked.
 */
function gateRows(gate: Gate | null): Check[] {
  const checks = gate?.checks ?? []
  const known = CHECK_ORDER.map((code) => checks.find((check) => check.code === code)).filter(
    (check): check is Check => check !== undefined,
  )
  return [...known, ...checks.filter((check) => !CHECK_ORDER.includes(check.code))]
}

/** A string off the free-form `quality` object, or null. `fail_draft` puts the
 *  generation's own error on `reason`, where no other shape has it. */
function reasonOf(detail: Detail | null): string | null {
  const value = (detail?.quality ?? {})['reason']
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

/**
 * The content pipeline — S-D5's `seo.html#pipeline`, built in S-G6 over S-B25
 * and S-B26.
 *
 * **Seven endpoints in `app/v1/seo_content.py` had no caller at all**: the board
 * itself (`GET /sites/{id}/content`), the run history, one document with its
 * trail, the quality thresholds both ways, the re-check and the rewrite. Audit
 * row §A2, with §G13 — *no screen shows a gate result* — as the half of it the
 * approval queue felt.
 *
 * WHY THE BRIEF FORM AND THE THRESHOLDS ARE ON THIS SCREEN
 * --------------------------------------------------------
 * **A brief has exactly one writer.** `create_brief` is reached only from
 * `ContentService.submit`, which is queued only by `POST /sites/{id}/content` or
 * by the CLI — so without a form on this screen the *Briefed* column can never
 * hold anything a tenant put there, and the board would be a window onto work
 * only an operator could start.
 *
 * **The gate's limits are read where its verdicts are read.** `read_quality`'s
 * docstring imagines them on the settings screen beside the brand voice, and
 * that would mean a ninth settings panel — the ninth thing S-D3 did not approve,
 * which is the call `panels.ts` refused for webhooks and `PublishTargets` for
 * itself. §D1 already settled the shape of this argument for the approval
 * gating: *the rules are read where the queue they govern is read*. A threshold
 * only means something next to the verdict it produced.
 *
 * WHAT THE MOCKUP DRAWS AND THIS DOES NOT
 * ----------------------------------------
 * **"Let me review it anyway"** would move a blocked draft into the approval
 * queue over the gate's objection, and no endpoint does that: `GATEABLE_STATUSES`
 * is the set the gate may write over, and nothing overrides a verdict. What
 * exists is *Run the gate again*, which is the honest version of the same
 * intent — it re-judges against the limits in force **now**, so a tenant who
 * disagrees with a block raises the limit above and presses it.
 *
 * **"Discard"** would delete a document, and there is no DELETE either. Both are
 * recorded in `PROGRESS.md` rather than rendered as buttons that do nothing —
 * the call S-G5 made about *Send a test post* and *Disconnect*.
 */
export function ContentPipeline(): JSX.Element {
  const brand = useBrand()
  const { membership } = useActiveTenant()
  // `discovery:manage` on the server for all three presses, and its docstring
  // gives the reason: each one spends money against the budget cap. Changing the
  // gate is `tenant:update` and lives on `/settings/content-quality` now.
  const mayRun = may(membership, 'discovery:manage')
  const [siteId, setSiteId] = useState<string | null>(null)

  const sites = useQuery((signal) => call(() => api.GET('/api/v1/seo/sites', { signal })), [])
  const listed = sites.state.data?.sites ?? []
  const selectedId = siteId ?? listed[0]?.id ?? null
  const site = listed.find((row) => row.id === selectedId) ?? null

  if (sites.state.status === 'loading') return <Loading label="Loading your sites" />
  if (sites.state.status === 'error') {
    return <ErrorState failure={sites.state.failure} onRetry={sites.refetch} />
  }

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row">
        <h1 className="ar-h1">Content pipeline</h1>
        <span className="app-spacer" />
        {listed.length > 1 ? (
          // SEO-UI-49's fix, applied here. **SF-26 found this and named this screen
          // in its handoff**: `.ar-select` is `width: 100%` in the atlas and `.ar-row`
          // is `flex-wrap: wrap`, so a bare one claims the whole line and puts the h1
          // and the picker on rows of their own — a two-site tenant read a header two
          // lines tall. It is in scope here because it is in this session's file and
          // it is the same defect, not a second one.
          <select
            className="ar-select app-page-select"
            aria-label="Which site"
            value={selectedId ?? ''}
            onChange={(event) => {
              setSiteId(event.target.value)
            }}
          >
            {listed.map((row) => (
              <option key={row.id} value={row.id}>
                {row.domain}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {site === null ? (
        <div className="ar-card">
          <div className="ar-card__body">
            <Empty
              title="No site yet"
              body={`Add your website first. ${brand.productName} writes for a site — its brand voice, its existing pages and the keywords it is chasing all come from one.`}
            />
          </div>
        </div>
      ) : (
        // Keyed on the site so switching remounts everything: a half-typed brief
        // must not survive into another site's form, and the selected document
        // belongs to the site it was picked on. The same reason
        // `PublishTargets` keys `TargetSetup`.
        <Board key={site.id} siteId={site.id} domain={site.domain} mayRun={mayRun} />
      )}

      <Thresholds />
    </div>
  )
}

/* --- the board ------------------------------------------------------------------ */

/**
 * One site's board, the document it is showing, and everything under it.
 *
 * The document list is fetched once and bucketed here rather than four requests
 * for four columns: the statuses are disjoint, the whole board is one site's
 * pipeline, and `?status=` is repeatable precisely so one read can ask for all
 * of them.
 */
function Board({
  siteId,
  domain,
  mayRun,
}: {
  siteId: string
  domain: string
  mayRun: boolean
}): JSX.Element {
  const [picked, setPicked] = useState<string | null>(null)
  // **Read once per render, not per card.** SEO-UI-61's *"waiting 2 days"* is a
  // distance from *now*, and a `new Date()` inside `captionOf` would let two cards
  // in one paint measure against two different clocks — which is invisible at the
  // day granularity this rounds to, and exactly the kind of thing that makes a
  // test flake at a minute boundary. It is deliberately not state: nothing here
  // ticks, and a board that re-rendered every second to move a caption from
  // *6 hours* to *7 hours* would be spending a paint on a number nobody watches.
  const now = new Date()

  const documents = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/sites/{site_id}/content', {
          params: { path: { site_id: siteId }, query: { status: PIPELINE } },
          signal,
        }),
      ),
    [siteId],
  )

  // Hoisted out of `Runs` so a press can refresh it. Briefing, rewriting and
  // re-checking all queue a *run*, and a history that did not move when one was
  // queued would tell a tenant their press did nothing — which is the reading
  // this screen most has to avoid, since the next thing they do is press again.
  const runs = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/sites/{site_id}/content/runs', {
          params: { path: { site_id: siteId } },
          signal,
        }),
      ),
    [siteId],
  )

  function reload(): void {
    documents.refetch()
    runs.refetch()
  }

  const rows = documents.state.data?.documents ?? []
  // What the detail panel opens on before anybody has clicked: whatever the gate
  // has, because that is the card the tenant is on this screen to read, and a
  // blocked draft is the only one with an action to take. Falling back to the
  // first document of any kind rather than to nothing, so the panel is never an
  // empty box beside a board with work on it.
  const gated = rows.find((row) => row.status === 'quality_failed' || row.status === 'quality_gate')
  // **A pick is only honoured while what it names is still on the board**, which is
  // the guard `ApprovalQueue` already applies to its own list and this screen did
  // not. *Discard* is what made it reachable: the document moves to `rejected` and
  // leaves the board, and a held `picked` kept the panel describing it — under a
  // *"Not judged yet"* badge, because `rejected` matches none of the header's
  // branches, which is the opposite of what the gate had said about it. Falling
  // back re-runs the same choice the panel opened on.
  const held = picked !== null && rows.some((row) => row.id === picked) ? picked : null
  const shown = held ?? gated?.id ?? rows[0]?.id ?? null
  // A full page is the only signal that there may be more — the same reading
  // `Suppression` makes of its own `LIMIT n` with no offset.
  const cappedDocuments = rows.length >= DOCUMENT_LIMIT

  return (
    <>
      {documents.state.status === 'loading' ? (
        <Loading label="Loading the pipeline" lines={4} />
      ) : documents.state.status === 'error' ? (
        <ErrorState failure={documents.state.failure} onRetry={documents.refetch} />
      ) : (
        /* The four columns are drawn whatever they hold (parity P-PIPE-01):
           each one says what it is waiting for, and the brief form below is how
           the first column fills. */
        <div className="ar-grid ar-grid--4">
          {COLUMNS.map((column) => {
            const held = rows.filter((row) =>
              (column.statuses as readonly string[]).includes(row.status),
            )
            return (
              <section className="ar-card" key={column.key} aria-label={column.label}>
                <div className="ar-card__header">
                  <h2>{column.label}</h2>
                  {held.length === 0 ? null : (
                    <>
                      <span className="ar-card__spacer" />
                      <span
                        className={`ar-badge ${column.key === 'awaiting' ? 'ar-badge--warning' : 'ar-badge--neutral'}`}
                      >
                        {held.length}
                      </span>
                    </>
                  )}
                </div>
                <div className="ar-card__body ar-stack ar-stack--tight">
                  {held.length === 0 ? (
                    <p className="app-board__meta" style={{ margin: 0 }}>
                      {column.empty}
                    </p>
                  ) : (
                    held.map((row) => (
                      <Card
                        key={row.id}
                        document={row}
                        current={row.id === shown}
                        now={now}
                        onPick={() => {
                          setPicked(row.id)
                        }}
                      />
                    ))
                  )}
                </div>
              </section>
            )
          })}
        </div>
      )}

      {cappedDocuments ? (
        <p className="ar-subtle ar-text-body-sm" style={{ margin: 0 }}>
          Showing the {DOCUMENT_LIMIT} most recent documents, which is as many as this board will
          load at once. Older ones are still in the pipeline.
        </p>
      ) : null}

      {shown === null ? null : (
        // Keyed on the document so the panel cannot render one article's gate
        // under another's heading for a frame — the defect `PublishTargets`
        // records for its own seeded form, in the shape it takes here.
        <GatePanel key={shown} contentId={shown} mayRun={mayRun} onChanged={reload} />
      )}

      <Brief siteId={siteId} domain={domain} mayRun={mayRun} onQueued={reload} />
      <Runs runs={runs} />
    </>
  )
}

/**
 * What a card is called. SEO-UI-61.
 *
 * **A briefed document has no title**, because the title is the model's first
 * decision and the model has not been called yet — so the whole first column read
 * *"Untitled brief"* on every card, which is precisely the column where the card
 * is the only thing that says what was asked for. `topic` is what the tenant typed
 * into the brief form, and it is on `ContentDocumentOut` for this.
 */
function nameOf(document: Document): string {
  const name = document.title ?? (document.topic === '' ? 'Untitled brief' : document.topic)
  // A rewrite is named by the page it improves: *"Rewrite of /about — About
  // Kavya"* — because two cards called *About Kavya* (the page and its rewrite)
  // would be the confusion the rewrite exists to avoid on the site itself.
  return document.rewrite_url ? `Rewrite of ${pathOf(document.rewrite_url)} — ${name}` : name
}

/** A URL's path, for a card: `/about`, or the host when it is the origin. */
function pathOf(url: string): string {
  try {
    const parsed = new URL(url)
    return parsed.pathname === '/' ? parsed.hostname : parsed.pathname
  } catch {
    return url
  }
}

/**
 * The caption under a card's name — what the design puts there, per column.
 * SEO-UI-61.
 *
 *  * **Briefed** — *"briefed 09:12"*. S-D5 also draws the keyword's search volume
 *    (*"12k/mo"*), and the drawing now says why this does not: a brief's terms are
 *    free text a tenant typed, and a volume exists only for a keyword they are
 *    *tracking* — bought from DataForSEO against the plan's allowance. Most briefs
 *    would render a blank where the number is, and the ones that did not would be
 *    quoting a figure for a term that is only spelled the same.
 *  * **Writing** — *"Batch API · returns 04:00"*, from the batch's own deadline.
 *  * **Awaiting you** — *"waiting 2 days"*, which is the question that column asks.
 *  * Everything else keeps the word count and the timestamp it had.
 */
function captionOf(document: Document, now: Date): string {
  if (document.status === 'brief') {
    // `formatClock`, not `formatTime`: that one carries seconds for the reasoning
    // trail, which needs them, and *"briefed 09:12:34"* is three digits of precision
    // on a time nobody reconciles against anything.
    return `briefed ${formatClock(document.updated_at, IST)}`
  }
  if (document.status === 'awaiting_approval') {
    const waited = elapsed(document.updated_at, now)
    return waited === null ? 'waiting — just arrived' : `waiting ${waited}`
  }
  if (document.status === 'drafting') {
    // **The deadline is when the Batch API gives up, not an estimate of when the
    // article lands** — Anthropic expires a batch at 24 hours and most return long
    // before that. *"back by"* is the honest reading of the only number there is;
    // *"returns 04:00"* as drawn would be a promise nothing measured.
    return document.batch_deadline_at
      ? `Batch API · back by ${formatDateTime(document.batch_deadline_at, IST)}`
      : 'Batch API · still being written'
  }
  const words = document.word_count > 0 ? `${String(document.word_count)} words · ` : ''
  return `${words}${formatDateTime(document.updated_at, IST)}`
}

/**
 * The badge on a blocked card, naming the check that objected. SEO-UI-81.
 *
 * S-D5 draws *"✕ Failed fact-check"* and the screen said *"✕ Blocked by the
 * gate"* — which is the one thing a reader of that card already knows, since the
 * card is in the column called *Quality gate*. **Which check objected is what
 * decides what they do next**: a fact-check block is answered by a rewrite, a
 * similarity block by raising a limit, and an E-E-A-T block usually by neither.
 *
 * More than one failing check names the first and counts the rest, rather than
 * running off the card. Falls back to the gate when the document is from before
 * per-check verdicts were stored — `failed_checks` is empty there, and inventing
 * a check that never ran would be worse than the general sentence.
 */
function blockLabel(document: Document): string {
  const failed = document.failed_checks ?? []
  const first = failed[0]
  if (first === undefined) return '✕ Blocked by the gate'
  // **The label as written, not lower-cased.** `toLowerCase` read well on *Fact
  // check* and turned E-E-A-T into *"Failed e-e-a-t"*, which reads as a rendering
  // fault rather than as a verdict — on the check most likely to need explaining to
  // the person reading it. The map's labels are already the sentence case a badge
  // wants.
  const label = CHECKS[first] ?? first
  return failed.length === 1
    ? `✕ Failed ${label}`
    : `✕ Failed ${label} +${String(failed.length - 1)}`
}

/** One document on the board. A button rather than a div: it is the control that
 *  chooses what the panel below describes, so it has to be reachable by keyboard
 *  and announced as the current one. */
function Card({
  document,
  current,
  now,
  onPick,
}: {
  document: Document
  current: boolean
  now: Date
  onPick: () => void
}): JSX.Element {
  return (
    <button type="button" className="app-board__pick" aria-current={current} onClick={onPick}>
      <strong>{nameOf(document)}</strong>
      {/* SEO-UI-60. The skeleton is the affordance S-D5 draws on the *Writing*
          column and nowhere else, and it is the only column where it is honest:
          a batch genuinely is in progress and there is genuinely nothing to show
          yet. `aria-hidden`, because the caption beside it already says the same
          thing in words and a screen reader announcing a decorative bar twice is
          how a loading state becomes noise. */}
      {document.status === 'drafting' ? (
        <span className="ar-skeleton ar-skeleton--text app-board__wait" aria-hidden="true" />
      ) : null}
      <span className="app-board__meta">{captionOf(document, now)}</span>
      {document.status === 'quality_failed' ? (
        <span className="ar-badge ar-badge--danger">{blockLabel(document)}</span>
      ) : document.status === 'failed' ? (
        <span className="ar-badge ar-badge--danger">✕ Did not write</span>
      ) : document.status === 'quality_gate' ? (
        <span className="ar-badge ar-badge--neutral">Being checked</span>
      ) : document.blocked ? (
        <span className="ar-badge ar-badge--warning">⚠ Held</span>
      ) : null}
    </button>
  )
}

/* --- the quality gate ----------------------------------------------------------- */

/**
 * One document's gate verdict, and the two presses that answer it.
 *
 * **Rendered as loading until the answer in hand is about the document on
 * screen.** `state.status !== 'loading'` is not that question: picking a second
 * card changes `contentId` at once, and there is one render where the new title
 * is up and the previous document's response is still the query's data. This
 * panel is keyed on the id as well, which makes that render impossible — the two
 * together rather than either alone, because the key protects the mount and the
 * check protects a refetch.
 */
function GatePanel({
  contentId,
  mayRun,
  onChanged,
}: {
  contentId: string
  mayRun: boolean
  onChanged: () => void
}): JSX.Element {
  const toast = useToast()
  const [discarding, setDiscarding] = useState(false)

  const detail = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/content/{content_id}', {
          params: { path: { content_id: contentId } },
          signal,
        }),
      ),
    [contentId],
  )

  const recheck = useMutation(() =>
    call(() =>
      api.POST('/api/v1/seo/content/{content_id}/recheck', {
        params: { path: { content_id: contentId } },
      }),
    ),
  )

  const rewrite = useMutation(() =>
    call(() =>
      api.POST('/api/v1/seo/content/{content_id}/rewrite', {
        params: { path: { content_id: contentId } },
      }),
    ),
  )

  // SEO-UI-58. S-D5's *"Let me review it anyway"*. It does not re-judge and it
  // does not touch the organisation's limits — it moves this one draft into the
  // approval queue with the gate's objection still attached to it, so the person
  // who disagreed with the block is the person who reads it. `override_gate` on
  // the API argues why the alternative (lowering a threshold until it passes) was
  // the wrong instrument.
  const override = useMutation(() =>
    call(() =>
      api.POST('/api/v1/seo/content/{content_id}/override', {
        params: { path: { content_id: contentId } },
      }),
    ),
  )

  // SEO-UI-59. Nothing is deleted: the document moves to `rejected` and stays
  // readable, which is what `discard_document` is for and why this asks first.
  const discard = useMutation(() =>
    call(() =>
      api.POST('/api/v1/seo/content/{content_id}/discard', {
        params: { path: { content_id: contentId } },
      }),
    ),
  )

  const loaded = detail.state.data
  const pending = detail.state.status === 'loading' || (loaded !== null && loaded.id !== contentId)
  const gate = loaded?.gate ?? null
  const rows = gateRows(gate)
  const failure = reasonOf(loaded)
  // The two presses S-D5 puts beside *Rewrite* apply to a draft the gate stopped,
  // and `DISCARDABLE_STATUSES` on the API is the same pair from its end. A
  // document already in the approval queue is decided there; one that is published
  // is rolled back, not discarded.
  const blocked = loaded?.status === 'quality_failed'
  const discardable = blocked || loaded?.status === 'failed'

  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h2>Quality gate{loaded && !pending ? ` — “${loaded.title ?? 'Untitled brief'}”` : ''}</h2>
        {pending || loaded === null ? null : (
          <>
            <span className="ar-card__spacer" />
            {loaded.status === 'quality_failed' || gate?.blocked === true ? (
              <span className="ar-badge ar-badge--danger">✕ Blocked</span>
            ) : loaded.status === 'failed' ? (
              <span className="ar-badge ar-badge--danger">✕ Did not write</span>
            ) : loaded.status === 'awaiting_approval' ? (
              <span className="ar-badge ar-badge--success">✓ Passed</span>
            ) : (
              <span className="ar-badge ar-badge--neutral">Not judged yet</span>
            )}
          </>
        )}
      </div>

      <div className="ar-card__body ar-stack ar-stack--tight">
        {pending ? (
          <Loading label="Loading this draft’s quality gate" />
        ) : detail.state.status === 'error' ? (
          <ErrorState failure={detail.state.failure} onRetry={detail.refetch} />
        ) : loaded === null ? (
          <Empty
            title="Nothing selected"
            body="Pick an article on the board above to see what the quality gate made of it."
          />
        ) : (
          <>
            <InlineError
              failure={
                recheck.state.failure ??
                rewrite.state.failure ??
                override.state.failure ??
                discard.state.failure
              }
            />

            {rows.length === 0 ? (
              <Empty
                title="No gate result"
                body={
                  failure ??
                  'This draft has not been through the quality gate. Anything written before the gate existed, and anything still being written, has no verdict to show.'
                }
              />
            ) : (
              rows.map((check) => {
                const verdict = VERDICT[check.verdict] ?? { tone: 'ar-badge--neutral', mark: '·' }
                return (
                  <div className="app-gate__row" key={check.code}>
                    <span className={`ar-badge ${verdict.tone}`}>{verdict.mark}</span>
                    <span className="app-gate__grow">
                      <strong>{CHECKS[check.code] ?? check.code}</strong>
                      {check.reason === '' ? null : <span>{check.reason}</span>}
                    </span>
                    {check.headline === '' ? null : (
                      <span className="ar-mono">{check.headline}</span>
                    )}
                  </div>
                )
              })
            )}

            {(gate?.reasons ?? []).length === 0 ? null : (
              /* SEO-UI-80. `ar-stack--tight`'s 0.5rem gap put the alert hard
                 against the last check row, where S-D5 gives it 1.25rem — and the
                 spacing is doing work here rather than decorating: the rows above
                 are five findings and this is the verdict drawn from them, so a
                 gap that reads as *"and one more row"* is the wrong sentence. */
              <div className="ar-alert ar-alert--danger app-gate__verdict" role="status">
                <span className="ar-alert__icon" aria-hidden="true">
                  ✕
                </span>
                <div className="ar-alert__body">
                  <strong>Blocked before it reached you</strong>
                  {(gate?.reasons ?? []).map((reason) => (
                    <span className="app-board__meta" style={{ display: 'block' }} key={reason}>
                      {reason}
                    </span>
                  ))}
                  <span style={{ display: 'block' }}>
                    This never enters the approval queue. Rewrite it without what the gate objected
                    to, or change the limits below and run the gate again.
                  </span>
                </div>
              </div>
            )}

            {gate?.thresholds === null || gate?.thresholds === undefined ? null : (
              <p className="app-board__meta" style={{ margin: 0 }}>
                Judged against {String(gate.thresholds.max_similarity_pct)}% similarity,{' '}
                {String(gate.thresholds.max_unverifiable_claims)} unverifiable claims and an E-E-A-T
                score of {String(gate.thresholds.min_eeat_score)} — the limits in force when it ran.
              </p>
            )}

            {loaded.status === 'awaiting_approval' ? (
              <p className="app-board__meta" style={{ margin: 0 }}>
                It passed and is waiting for you in{' '}
                <Link className="ar-link" to="/seo/approvals">
                  the approval queue
                </Link>
                .
              </p>
            ) : null}
          </>
        )}
      </div>

      {/* `loaded !== null` is already the whole of "the read succeeded" — the
          query's state is a union, and `data` is non-null only on `ready`. */}
      {/* SEO-UI-82. S-D5's footer is *[Rewrite] [Let me review it anyway] — spacer
          — [Discard]*, and all four presses are here now: the two that spend a
          generation or a Haiku call, the one that sets the verdict aside, and the
          one that throws the draft away. The spacer is what puts *Discard* at the
          far end, away from the three a tenant reaches for on purpose. */}
      {!mayRun || pending || loaded === null ? null : (
        <div className="ar-card__footer">
          {blocked ? (
            <Button
              tone="primary"
              busy={rewrite.pending}
              busyLabel="Queueing"
              onClick={() => {
                void (async () => {
                  const result = await rewrite.run(undefined)
                  if (!result.ok) return
                  detail.refetch()
                  onChanged()
                  toast.show({
                    kind: 'success',
                    title: 'Writing it again',
                    body: "The gate's objections go to the model with the original brief. It costs another generation, so it happens once per press.",
                  })
                })()
              }}
            >
              Rewrite without what the gate objected to
            </Button>
          ) : null}
          <Button
            busy={recheck.pending}
            busyLabel="Queueing"
            onClick={() => {
              void (async () => {
                const result = await recheck.run(undefined)
                if (!result.ok) return
                detail.refetch()
                onChanged()
                toast.show({
                  kind: 'success',
                  title: 'Judging it again',
                  body: 'Nothing is regenerated — the draft you have is re-judged against the limits in force now.',
                })
              })()
            }}
          >
            Run the gate again
          </Button>
          {blocked ? (
            <Button
              busy={override.pending}
              busyLabel="Sending"
              onClick={() => {
                void (async () => {
                  const result = await override.run(undefined)
                  if (!result.ok) return
                  detail.refetch()
                  onChanged()
                  toast.show({
                    kind: 'success',
                    title: 'It is waiting for you in the approval queue',
                    body: "The gate's objection goes with it and your name is on the decision to look past it. Nothing publishes until you approve it there.",
                  })
                })()
              }}
            >
              Let me review it anyway
            </Button>
          ) : null}
          {discardable ? (
            <>
              <span className="ar-card__spacer" />
              <Button
                tone="quiet"
                busy={discard.pending}
                busyLabel="Discarding"
                onClick={() => {
                  setDiscarding(true)
                }}
              >
                Discard
              </Button>
            </>
          ) : null}
        </div>
      )}

      {/* **A confirmation, where the design draws a bare button.** Discarding is
          the only press on this screen that ends an article, and it ends one the
          organisation paid Anthropic to write — the same reason `Keywords` puts a
          modal in front of *Stop tracking*. The modal says what survives, because
          *"discard"* reads as *delete* and this is not one. */}
      {discarding && loaded !== null ? (
        <Modal
          title="Discard this draft?"
          onClose={() => {
            setDiscarding(false)
          }}
          footer={
            <>
              <Button
                tone="ghost"
                onClick={() => {
                  setDiscarding(false)
                }}
              >
                Keep it
              </Button>
              <Button
                tone="danger"
                busy={discard.pending}
                busyLabel="Discarding"
                onClick={() => {
                  void (async () => {
                    const result = await discard.run(undefined)
                    if (!result.ok) return
                    setDiscarding(false)
                    onChanged()
                    toast.show({
                      kind: 'success',
                      title: 'Discarded',
                      body: 'It leaves the board. The text and what it cost stay on the record.',
                    })
                  })()
                }}
              >
                Discard
              </Button>
            </>
          }
        >
          <InlineError failure={discard.state.failure} />
          <p style={{ marginTop: 0 }}>
            “{nameOf(loaded)}” leaves the board and nothing more is spent on it. Nothing is deleted
            — the text, the gate&apos;s verdict and what the generation cost stay on the record, so
            this run can still account for what it bought.
          </p>
        </Modal>
      ) : null}
    </section>
  )
}

/* --- asking for one -------------------------------------------------------------- */

/** `ContentBriefIn`'s own default, restated so the form and the server agree
 *  about what an untouched length field means. */
const DEFAULT_WORDS = 1200

/**
 * The brief — the only way a tenant can put anything in the *Briefed* column.
 *
 * A 202 with `queued: false` is **not** an error and is not shown as one: it
 * means a run for this site was already in flight and was handed back instead,
 * which is `write_article` refusing to spend twice on one press. The toast says
 * so rather than the screen claiming a second article is coming.
 */
function Brief({
  siteId,
  domain,
  mayRun,
  onQueued,
}: {
  siteId: string
  domain: string
  mayRun: boolean
  onQueued: () => void
}): JSX.Element {
  const brand = useBrand()
  const toast = useToast()
  // *Improve this page*: the Keywords screen arrives with `?rewrite=` (the
  // page) and `?topic=` (the term), which pre-fill the brief once.
  const [searchParams, setSearchParams] = useSearchParams()
  const prefilled = searchParams.get('topic') ?? ''
  const [topic, setTopic] = useState(prefilled)
  const [keywords, setKeywords] = useState(prefilled)
  const [intent, setIntent] = useState('')
  const [audience, setAudience] = useState('')
  const [words, setWords] = useState(String(DEFAULT_WORDS))
  const [rewriteUrl, setRewriteUrl] = useState(searchParams.get('rewrite') ?? '')

  const submit = useMutation((body: Schema<'ContentBriefIn'>) =>
    call(() =>
      api.POST('/api/v1/seo/sites/{site_id}/content', {
        params: { path: { site_id: siteId } },
        body,
      }),
    ),
  )

  const length = Number(words)
  // Bounded here as well as on the server, because the failure is expensive in
  // the wrong direction: `ContentBriefIn` refuses 300–3000 with a 422, and a
  // tenant who types 5000 should be told before a request goes out rather than
  // read a validation envelope about a field they can see.
  const lengthError =
    words.trim() === '' || !Number.isFinite(length) || length < 300 || length > 3000
      ? 'Between 300 and 3,000 words.'
      : undefined
  const topicError =
    topic.trim() !== '' && topic.trim().length < 3 ? 'At least three characters.' : undefined
  const rewriteError = rewriteUrlError(rewriteUrl, domain)
  const ready = topic.trim().length >= 3 && lengthError === undefined && rewriteError === undefined
  const rewriting = rewriteUrl.trim() !== ''

  if (!mayRun) {
    return (
      <section className="ar-card">
        <div className="ar-card__header">
          <h2>Write something new</h2>
        </div>
        <div className="ar-card__body">
          <Empty
            title="An owner or an admin asks for articles"
            body="Writing one spends against this organisation's budget cap, so the roles that manage spending are the ones that can start it. You can follow everything on the board above."
          />
        </div>
      </section>
    )
  }

  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h2>Write something new</h2>
      </div>
      <div className="ar-card__body ar-stack">
        <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
          A brief for <strong>{domain}</strong>. It goes to the Batch API, which is half the price
          and returns in hours rather than seconds — the article appears under <em>Writing</em> and
          moves along the board on its own.
        </p>

        <InlineError failure={submit.state.failure} />

        <Field
          label="A page to improve"
          error={rewriteError}
          help={`Optional. Paste the address of a page already on ${domain} and ${brand.productName} rewrites that page — keeping every fact on it — instead of writing a new one. The approval shows the change as a diff, and publishing updates the page in place.`}
        >
          {(aria) => (
            <input
              className="ar-input"
              {...aria}
              inputMode="url"
              placeholder={`https://${domain}/…`}
              value={rewriteUrl}
              onChange={(event) => {
                setRewriteUrl(event.currentTarget.value)
                submit.reset()
              }}
            />
          )}
        </Field>

        <Field
          label="What it is about"
          required
          error={topicError}
          help="One line. “Modular kitchen cost in Hyderabad”, not a title."
        >
          {(aria) => (
            <input
              className="ar-input"
              {...aria}
              maxLength={300}
              value={topic}
              onChange={(event) => {
                setTopic(event.currentTarget.value)
                submit.reset()
              }}
            />
          )}
        </Field>

        <Field
          label="Terms it should rank for"
          help={`Comma separated, most important first. Up to ten; leave it empty to let ${brand.productName} choose from what you already track.`}
        >
          {(aria) => (
            <input
              className="ar-input"
              {...aria}
              value={keywords}
              onChange={(event) => {
                setKeywords(event.currentTarget.value)
                submit.reset()
              }}
            />
          )}
        </Field>

        <Field
          label="What the piece is for"
          help="In your own words. “Convince somebody comparing quotes”, “answer the price question before the call”."
        >
          {(aria) => (
            <input
              className="ar-input"
              {...aria}
              maxLength={500}
              value={intent}
              onChange={(event) => {
                setIntent(event.currentTarget.value)
                submit.reset()
              }}
            />
          )}
        </Field>

        <Field label="Who it is for" help="Optional. Left empty, your brand voice decides.">
          {(aria) => (
            <input
              className="ar-input"
              {...aria}
              maxLength={300}
              value={audience}
              onChange={(event) => {
                setAudience(event.currentTarget.value)
                submit.reset()
              }}
            />
          )}
        </Field>

        <Field label="How long" error={lengthError} help="Words. Longer costs more to write.">
          {(aria) => (
            <input
              className="ar-input"
              {...aria}
              inputMode="numeric"
              value={words}
              onChange={(event) => {
                setWords(event.currentTarget.value)
                submit.reset()
              }}
            />
          )}
        </Field>
      </div>
      <div className="ar-card__footer">
        <Button
          tone="primary"
          disabled={!ready}
          busy={submit.pending}
          busyLabel="Queueing"
          onClick={() => {
            void (async () => {
              const result = await submit.run({
                topic: topic.trim(),
                keywords: keywords
                  .split(',')
                  .map((word) => word.trim())
                  .filter((word) => word !== '')
                  .slice(0, 10),
                intent: intent.trim(),
                audience: audience.trim(),
                word_count: length,
                rewrite_url: rewriteUrl.trim(),
              })
              if (!result.ok) return
              setTopic('')
              setKeywords('')
              setIntent('')
              setAudience('')
              setWords(String(DEFAULT_WORDS))
              setRewriteUrl('')
              if (searchParams.has('rewrite') || searchParams.has('topic')) {
                searchParams.delete('rewrite')
                searchParams.delete('topic')
                setSearchParams(searchParams, { replace: true })
              }
              onQueued()
              toast.show({
                kind: 'success',
                title: result.data.queued
                  ? rewriting
                    ? 'Rewrite briefed'
                    : 'Briefed'
                  : 'A run was already going',
                body: result.data.queued
                  ? rewriting
                    ? 'The page is read first, then rewritten. It appears under Briefed, then Writing; the approval shows the change against the page as it is.'
                    : 'It appears under Briefed, then Writing. The Batch API usually returns overnight.'
                  : `${brand.productName} handed back the run already in flight rather than spending twice. Try again once it finishes.`,
              })
            })()
          }}
        >
          {rewriting ? 'Rewrite it' : 'Write it'}
        </Button>
      </div>
    </section>
  )
}

/* --- the limits ------------------------------------------------------------------ */

/**
 * Where the gate is set — read here, edited on `/settings/content-quality`.
 *
 * Settings consolidation, batch 2 (2026-09-15). The three limits and the
 * avoid-list switch were a form on this screen; it is `settings/content-quality/`
 * now, shared with the wizard. This card says what the gate is and links there.
 */
function Thresholds(): JSX.Element {
  const limits = useQuery(
    (signal) => call(() => api.GET('/api/v1/seo/content/quality', { signal })),
    [],
  )

  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h2>Where the gate is set</h2>
        <span className="ar-card__spacer" />
        <Link className="ar-btn ar-btn--ghost ar-btn--sm" to="/settings/content-quality">
          Change in Settings →
        </Link>
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight">
        {limits.state.status === 'loading' ? (
          <Loading label="Loading your quality gate" lines={1} />
        ) : null}
        {limits.state.status === 'error' ? (
          <ErrorState failure={limits.state.failure} onRetry={limits.refetch} />
        ) : null}
        {limits.state.status === 'ready' ? (
          <p style={{ margin: 0 }}>{summariseThresholds(limits.state.data)}</p>
        ) : null}
        <span className="ar-subtle ar-text-body-sm">
          One editorial standard for the whole organisation. A draft that fails any of these never
          reaches the approval queue.
        </span>
      </div>
    </section>
  )
}

/* --- the runs -------------------------------------------------------------------- */

/**
 * The generation runs behind the board.
 *
 * Here rather than on the reasoning trail because these are *content* runs — the
 * trail viewer is per pass and per site — and because a `failed` run carrying a
 * budget refusal is the answer to *"why has nothing appeared"*, which is a
 * question asked on this screen and nowhere else.
 */
function Runs({ runs }: { runs: Query<Schema<'ContentRunListResponse'>> }): JSX.Element {
  const rows: Run[] = runs.state.data?.runs ?? []
  const capped = rows.length >= RUN_LIMIT

  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h2>Recent runs</h2>
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight">
        {runs.state.status === 'loading' ? (
          <Loading label="Loading recent runs" />
        ) : runs.state.status === 'error' ? (
          <ErrorState failure={runs.state.failure} onRetry={runs.refetch} />
        ) : rows.length === 0 ? (
          <Empty
            title="No runs yet"
            body="Every generation this site has asked for will be listed here, with what it cost in time and why it stopped if it did."
          />
        ) : (
          rows.map((run) => {
            const state = RUN_STATE[run.status] ?? { tone: 'ar-badge--neutral', label: run.status }
            return (
              <div className="app-gate__row" key={run.id}>
                <span className={`ar-badge ${state.tone}`}>{state.label}</span>
                <span className="app-gate__grow">
                  <strong>{run.trigger === 'manual' ? 'You asked for it' : run.trigger}</strong>
                  <span>
                    {run.started_at === null || run.started_at === undefined
                      ? 'Not started yet'
                      : `Started ${formatDateTime(run.started_at, IST)}`}
                    {run.finished_at ? ` · finished ${formatDateTime(run.finished_at, IST)}` : ''}
                  </span>
                  {run.error === null || run.error === undefined ? null : (
                    <span className="app-pub__error">{run.error}</span>
                  )}
                </span>
              </div>
            )
          })
        )}
        {capped ? (
          <p className="ar-subtle ar-text-body-sm" style={{ margin: 0 }}>
            Showing the {RUN_LIMIT} most recent runs.
          </p>
        ) : null}
      </div>
    </section>
  )
}
